import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { startOfMonth, endOfMonth, startOfYear, endOfYear, isWithinInterval } from 'date-fns';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import './Dashboard.css';

export default function Dashboard() {
  const { products, orders, loading, refreshData, analytics } = useData();
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customRange, setCustomRange] = useState({
    startDate: '',
    endDate: ''
  });
  const location = useLocation();

  // Filtrer les commandes en attente
  const pendingOrders = useMemo(() => {
    return orders.filter(order => order.status === 'pending')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [orders]);

  // Fonction pour mettre à jour le statut d'une commande
  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const order = orders.find(o => o.id === orderId);
      const product = products.find(p => p.id === order.product.id);
      
      if (!product) {
        throw new Error('Produit non trouvé');
      }

      let stockAdjustment = 0;
      const currentStatus = order.status;

      // Calculer l'ajustement du stock
      if (['pending', 'confirmed'].includes(currentStatus) && 
          ['shipped', 'delivered'].includes(newStatus)) {
        // Réduire le stock quand on passe à expédié/livré
        stockAdjustment = -order.product.quantity;
      } 
      else if (['shipped', 'delivered'].includes(currentStatus) && 
               ['pending', 'confirmed', 'cancelled', 'returned'].includes(newStatus)) {
        // Remettre le stock quand on revient à en attente/confirmé ou annulé/retourné
        stockAdjustment = +order.product.quantity;
      }

      // Vérifier si le stock est suffisant
      if (stockAdjustment < 0 && (product.stock + stockAdjustment) < 0) {
        toast.error('Stock insuffisant pour cette opération');
        return;
      }

      // Mettre à jour le stock si nécessaire
      if (stockAdjustment !== 0) {
        await updateDoc(doc(db, 'products', product.id), {
          stock: product.stock + stockAdjustment
        });
      }

      // Mettre à jour le statut de la commande
      await updateDoc(doc(db, 'orders', orderId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });

      // Message de succès personnalisé
      let successMessage;
      switch (newStatus) {
        case 'confirmed':
          successMessage = 'Commande confirmée avec succès !';
          break;
        case 'shipped':
          successMessage = 'Commande marquée comme expédiée !';
          break;
        case 'delivered':
          successMessage = 'Commande marquée comme livrée !';
          break;
        case 'returned':
          successMessage = 'Commande marquée comme retournée !';
          break;
        case 'cancelled':
          successMessage = 'Commande annulée avec succès !';
          break;
        default:
          successMessage = 'Statut de la commande mis à jour !';
      }

      toast.success(successMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      // Rafraîchir les données
      refreshData();

    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast.error('Une erreur est survenue lors de la mise à jour');
    }
  };

  // Fonction pour filtrer les commandes par période
  const filterOrdersByPeriod = (orders) => {
    const now = new Date();
    let startDate, endDate;

    switch (periodFilter) {
      case 'month':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'year':
        startDate = startOfYear(now);
        endDate = endOfYear(now);
        break;
      case 'custom':
        startDate = customRange.startDate ? new Date(customRange.startDate) : null;
        endDate = customRange.endDate ? new Date(customRange.endDate) : null;
        break;
      default:
        return orders;
    }

    if (periodFilter === 'custom' && (!startDate || !endDate)) {
      return orders;
    }

    return orders.filter(order => {
      const orderDate = new Date(order.createdAt);
      return isWithinInterval(orderDate, { start: startDate, end: endDate });
    });
  };

  // Calcul des statistiques avec filtre de période
  const filteredOrders = filterOrdersByPeriod(orders);
  const stats = {
    totalRevenue: filteredOrders
      .filter(o => ['confirmed', 'shipped', 'delivered'].includes(o.status))
      .reduce((sum, order) => sum + order.product.subtotal, 0),
    
    totalOrders: filteredOrders.length,
    pendingOrders: filteredOrders.filter(o => o.status === 'pending').length,
    deliveredOrders: filteredOrders.filter(o => o.status === 'delivered').length,
    lowStockProducts: products.filter(p => p.stock < 10).length,
    activeProducts: products.filter(p => p.isActive).length
  };

  // Commandes récentes
  const recentOrders = [...orders]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  // Produits en rupture de stock
  const lowStockProducts = products
    .filter(p => p.stock < 10)
    .slice(0, 5);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement du tableau de bord...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dashboard-container">
        <h1>Tableau de bord</h1>

        {/* Filtres de période */}
        <div className="period-filters">
          <select 
            value={periodFilter} 
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="period-select"
          >
            <option value="all">Toutes les périodes</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
            <option value="custom">Période personnalisée</option>
          </select>

          {periodFilter === 'custom' && (
            <div className="date-range-picker">
              <input
                type="date"
                value={customRange.startDate}
                onChange={(e) => setCustomRange(prev => ({ 
                  ...prev, 
                  startDate: e.target.value 
                }))}
                className="date-input"
              />
              <input
                type="date"
                value={customRange.endDate}
                onChange={(e) => setCustomRange(prev => ({ 
                  ...prev, 
                  endDate: e.target.value 
                }))}
                min={customRange.startDate}
                className="date-input"
              />
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <i className="fas fa-shopping-cart"></i>
            <div className="stat-content">
              <h3>Commandes Totales</h3>
              <p>{stats.totalOrders}</p>
            </div>
          </div>

          <div className="stat-card">
            <i className="fas fa-money-bill-wave"></i>
            <div className="stat-content">
              <h3>Revenu {periodFilter === 'month' ? 'du mois' : 
                         periodFilter === 'year' ? 'de l\'année' : 
                         periodFilter === 'custom' ? 'de la période' : 'total'}</h3>
              <p>{stats.totalRevenue.toFixed(2)} DZD</p>
            </div>
          </div>

          <div className="stat-card">
            <i className="fas fa-clock"></i>
            <div className="stat-content">
              <h3>Commandes en Attente</h3>
              <p>{stats.pendingOrders}</p>
            </div>
          </div>

          <div className="stat-card">
            <i className="fas fa-box"></i>
            <div className="stat-content">
              <h3>Produits Actifs</h3>
              <p>{stats.activeProducts}</p>
            </div>
          </div>

          <div className="stat-card">
            <i className="fas fa-eye"></i>
            <div className="stat-content">
              <h3>Visites Totales</h3>
              <p>{analytics.totalVisits}</p>
            </div>
          </div>
        </div>

        {/* Sections du tableau de bord */}
        <div className="dashboard-sections">
          {/* Commandes récentes */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Commandes Récentes</h2>
              <button className="view-all">Voir tout</button>
            </div>
            <div className="recent-orders">
              {recentOrders.map(order => (
                <div key={order.id} className="order-item">
                  <div className="order-info">
                    <span className="order-id">#{order.id.slice(-6)}</span>
                    <span className="order-customer">{order.firstName} {order.lastName}</span>
                  </div>
                  <div className="order-details">
                    <span className="order-amount">{order.totalPrice} DZD</span>
                    <span className={`order-status ${order.status}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Produits en rupture de stock */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Stock Faible</h2>
              <button className="view-all">Voir tout</button>
            </div>
            <div className="low-stock-products">
              {lowStockProducts.map(product => (
                <div key={product.id} className="product-item">
                  <div className="product-info">
                    <span className="product-name">{product.name}</span>
                    <span className="product-category">
                      {product.categoryName}
                    </span>
                  </div>
                  <div className="stock-info">
                    <span className={`stock-level ${product.stock === 0 ? 'out' : 'low'}`}>
                      {product.stock === 0 ? 'Rupture' : `${product.stock} en stock`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Commandes en attente */}
          <div className="dashboard-section pending-orders">
            <h2>Commandes en attente</h2>
            <div className="pending-orders-grid">
              {pendingOrders.map(order => (
                <div key={order.id} className="pending-order-card">
                  <div className="order-header">
                    <span className="order-id">#{order.id.slice(-6)}</span>
                    <span className="order-date">
                      {new Date(order.createdAt).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  <div className="customer-info">
                    <h3>Client</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <i className="fas fa-user"></i>
                        <span>{order.customer.firstName} {order.customer.lastName}</span>
                      </div>
                      <div className="info-item">
                        <i className="fas fa-phone"></i>
                        <span>{order.customer.phone}</span>
                      </div>
                      <div className="info-item">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{order.customer.wilaya}, {order.customer.city}</span>
                      </div>
                    </div>
                  </div>

                  <div className="order-details">
                    <h3>Détails de la commande</h3>
                    <div className="product-info">
                      <span className="product-name">{order.product.name}</span>
                      <div className="product-details">
                        <span>{order.product.quantity}x {order.product.price} DZD</span>
                        <span className="subtotal">Sous-total: {order.product.subtotal} DZD</span>
                      </div>
                    </div>
                  </div>

                  <div className="shipping-info">
                    <h3>Livraison</h3>
                    <div className="delivery-type">
                      <i className={`fas fa-${order.shipping.type === 'home' ? 'home' : 'building'}`}></i>
                      <span>
                        {order.shipping.type === 'home' ? 'Livraison à domicile' : 'Récupération au bureau'}
                      </span>
                    </div>
                    <div className="shipping-cost">
                      {order.shipping.isFree ? (
                        <div className="free-shipping">
                          <i className="fas fa-gift"></i>
                          <span>Livraison gratuite</span>
                          <small>({order.shipping.freeReason})</small>
                        </div>
                      ) : (
                        <span>Frais de livraison: {order.shipping.price} DZD</span>
                      )}
                    </div>
                  </div>

                  <div className="order-total">
                    <span>Total</span>
                    <strong>{order.totalPrice} DZD</strong>
                  </div>

                  <div className="order-actions">
                    <button 
                      className="btn-confirm"
                      onClick={() => handleStatusUpdate(order.id, 'confirmed')}
                    >
                      <i className="fas fa-check"></i>
                      Confirmer
                    </button>
                    <button 
                      className="btn-cancel"
                      onClick={() => handleStatusUpdate(order.id, 'cancelled')}
                    >
                      <i className="fas fa-times"></i>
                      Annuler
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pages les plus visitées */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Pages les plus visitées</h2>
            </div>
            <div className="analytics-grid">
              {Object.entries(analytics.pageViews)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5)
                .map(([path, views]) => (
                  <div key={path} className="analytics-item">
                    <div className="analytics-info">
                      <span className="page-path">{path}</span>
                      <span className="view-count">{views} vues</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress" 
                        style={{ 
                          width: `${(views / Math.max(...Object.values(analytics.pageViews))) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Produits les plus consultés */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Produits les plus consultés</h2>
            </div>
            <div className="analytics-grid">
              {Object.entries(analytics.productViews)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5)
                .map(([productId, views]) => {
                  const product = products.find(p => p.id === productId);
                  return product ? (
                    <div key={productId} className="analytics-item">
                      <div className="analytics-info">
                        <span className="product-name">{product.name}</span>
                        <span className="view-count">{views} vues</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress" 
                          style={{ 
                            width: `${(views / Math.max(...Object.values(analytics.productViews))) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  ) : null;
                })}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
} 